import { BASE_URL } from "../../lib/api";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Lightbulb, Search } from "lucide-react";
import { CodeBlock } from "../../components/kit";
import { Input } from "../../components/ui/input";
import { CATEGORIES, GUIDES } from "../../lib/guides";
import { useT } from "../../lib/i18n";
import { useSeo } from "../../lib/seo";

const BASE = BASE_URL;
const UI = {
  en: { eyebrow: "Knowledge base", title: "Guides", desc: "Everything you need to send well: setup, deliverability, the API, campaigns and compliance.", search: "Search guides", min: "min read", back: "All guides", next: "Next", none: "No guides match your search." },
  fi: { eyebrow: "Tietopankki", title: "Oppaat", desc: "Kaikki mitä tarvitset hyvään lähettämiseen: käyttöönotto, toimitettavuus, API, kampanjat ja vaatimukset.", search: "Hae oppaista", min: "min lukuaika", back: "Kaikki oppaat", next: "Seuraava", none: "Haullasi ei löytynyt oppaita." },
};

function Inline({ text }) {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g).filter(Boolean);
  return parts.map((p, i) => {
    if (p.startsWith("`")) return <code key={i} className="rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[0.85em] text-purple-200">{p.slice(1, -1)}</code>;
    if (p.startsWith("**")) return <strong key={i} className="font-semibold text-white">{p.slice(2, -2)}</strong>;
    const m = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (m) return <Link key={i} to={m[2]} className="text-purple-300 underline decoration-purple-500/40 underline-offset-4 hover:text-purple-200">{m[1]}</Link>;
    return <span key={i}>{p}</span>;
  });
}

function blocks(md) {
  const out = [];
  const lines = md.replaceAll("https://YOUR_HOST", BASE).split("\n");
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (l.startsWith("```")) {
      const code = [];
      for (i++; i < lines.length && !lines[i].startsWith("```"); i++) code.push(lines[i]);
      out.push({ t: "code", v: code.join("\n") });
    } else if (l.startsWith("## ")) out.push({ t: "h", v: l.slice(3) });
    else if (l.startsWith("> ")) out.push({ t: "tip", v: l.slice(2) });
    else if (/^(- |\d+\. )/.test(l)) {
      const last = out[out.length - 1];
      const item = l.replace(/^(- |\d+\. )/, "");
      if (last?.t === "list") last.v.push(item); else out.push({ t: "list", v: [item], ordered: /^\d/.test(l) });
    } else if (l.trim()) out.push({ t: "p", v: l });
  }
  return out;
}

export function Body({ md }) {
  return blocks(md).map((b, i) => {
    if (b.t === "h") return <h2 key={i} id={`s-${i}`} className="mt-10 font-display text-xl text-white sm:text-2xl">{b.v}</h2>;
    if (b.t === "code") return <div key={i} className="my-5"><CodeBlock code={b.v} testId={`guide-code-${i}`} /></div>;
    if (b.t === "tip") return <div key={i} className="my-6 flex gap-3 rounded-xl border border-purple-400/20 bg-purple-500/[0.07] p-4 text-sm text-purple-50"><Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-purple-300" /><span><Inline text={b.v} /></span></div>;
    if (b.t === "list") {
      const Tag = b.ordered ? "ol" : "ul";
      return <Tag key={i} className={`my-4 space-y-2 pl-5 text-zinc-300 ${b.ordered ? "list-decimal" : "list-disc"} marker:text-purple-400`}>{b.v.map((x, j) => <li key={j}><Inline text={x} /></li>)}</Tag>;
    }
    return <p key={i} className="my-4 leading-relaxed text-zinc-300"><Inline text={b.v} /></p>;
  });
}

export function Guides() {
  const { lang } = useT();
  const ui = UI[lang] || UI.en;
  const [q, setQ] = useState("");
  useSeo({ title: lang === "fi" ? "Oppaat: sähköpostimarkkinointi ja toimitettavuus" : "Guides: email marketing, deliverability and setup",
    description: ui.desc, path: "/guides" });
  const list = useMemo(() => GUIDES.filter((g) => !q || `${g.title[lang]} ${g.summary[lang]} ${g.body[lang]}`.toLowerCase().includes(q.toLowerCase())), [q, lang]);
  return (
    <div className="relative overflow-x-hidden"><div className="aurora opacity-20" />
      <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="eyebrow mb-3">{ui.eyebrow}</div>
        <h1 className="text-4xl font-semibold text-white sm:text-5xl lg:text-6xl" data-testid="page-title">{ui.title}</h1>
        <p className="mt-4 max-w-2xl text-zinc-400">{ui.desc}</p>
        <div className="relative mt-8 max-w-md"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={ui.search} className="pl-9" data-testid="guides-search" /></div>
        {!list.length && <p className="mt-10 text-zinc-500" data-testid="guides-empty">{ui.none}</p>}
        {Object.entries(CATEGORIES).map(([key, name]) => {
          const items = list.filter((g) => g.cat === key);
          if (!items.length) return null;
          return (
            <section key={key} className="mt-14" data-testid={`guides-cat-${key}`}>
              <h2 className="mb-5 text-xs font-semibold uppercase tracking-[0.2em] text-purple-300">{name[lang]}</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{items.map((g) => (
                <Link key={g.slug} to={`/guides/${g.slug}`} className="surface group rounded-2xl p-6 transition-transform hover:-translate-y-0.5" data-testid={`guide-card-${g.slug}`}>
                  <div className="font-display text-lg leading-snug text-white group-hover:text-purple-100">{g.title[lang]}</div>
                  <p className="mt-2 text-sm text-zinc-400">{g.summary[lang]}</p>
                  <div className="mt-4 flex items-center gap-1.5 text-xs text-zinc-500"><Clock className="h-3.5 w-3.5" />{g.min} {ui.min}</div>
                </Link>))}</div>
            </section>);
        })}
      </div>
    </div>
  );
}

export function Guide() {
  const { slug } = useParams();
  const { lang } = useT();
  const ui = UI[lang] || UI.en;
  const idx = GUIDES.findIndex((g) => g.slug === slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  const g0 = GUIDES[idx];
  useSeo({ title: g0 ? g0.title[lang] : "Guides", description: g0 ? g0.summary[lang] : ui.desc, path: `/guides/${slug}`,
    article: g0 ? { published: "2026-06-01", lang } : null });
  if (idx < 0) return <Navigate to="/guides" replace />;
  const g = GUIDES[idx];
  const next = GUIDES[(idx + 1) % GUIDES.length];
  const related = GUIDES.filter((x) => x.cat === g.cat && x.slug !== g.slug);
  return (
    <div className="mx-auto grid max-w-6xl grid-cols-1 gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[minmax(0,1fr)_260px] lg:px-8 lg:py-20">
      <article data-testid="guide-article">
        <Link to="/guides" className="mb-6 inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="guide-back"><ArrowLeft className="h-4 w-4" />{ui.back}</Link>
        <div className="eyebrow mb-3">{CATEGORIES[g.cat][lang]}</div>
        <h1 className="text-3xl font-semibold leading-tight text-white sm:text-4xl lg:text-5xl" data-testid="page-title">{g.title[lang]}</h1>
        <div className="mt-4 flex items-center gap-1.5 text-xs text-zinc-500"><Clock className="h-3.5 w-3.5" />{g.min} {ui.min}</div>
        <div className="mt-6 text-[15px]"><Body md={g.body[lang]} /></div>
        <Link to={`/guides/${next.slug}`} className="surface mt-14 flex items-center justify-between rounded-2xl p-5 hover:bg-white/[0.04]" data-testid="guide-next">
          <span><span className="block text-xs text-zinc-500">{ui.next}</span><span className="text-white">{next.title[lang]}</span></span><ArrowRight className="h-5 w-5 text-purple-300" />
        </Link>
      </article>
      <aside className="space-y-2 lg:sticky lg:top-24 lg:self-start" data-testid="guide-related">
        <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{CATEGORIES[g.cat][lang]}</div>
        {related.map((r) => <Link key={r.slug} to={`/guides/${r.slug}`} className="block rounded-lg px-3 py-2 text-sm text-zinc-400 hover:bg-white/[0.04] hover:text-white">{r.title[lang]}</Link>)}
      </aside>
    </div>
  );
}
